import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/features/auth/AuthProvider'
import type { Cliente, CcMovimiento } from '@/types/database'
import { useClientes, useCcMovimientos, useRegistrarMovimientoCC } from './hooks'
import { CloseIcon, SearchIcon } from '@/components/shared/Icons'
import { useConfiguracion } from '@/features/configuracion/hooks'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

function formatWaPhone(tel: string | null): string | null {
  if (!tel) return null
  const d = tel.replace(/\D/g, '')
  if (!d) return null
  if (d.startsWith('54')) return d
  if (d.startsWith('0')) return '54' + d.slice(1)
  return '54' + d
}

function buildWaUrl(cliente: Cliente, nombreNegocio = 'nosotros'): string {
  const texto = encodeURIComponent(
    `Hola ${cliente.nombre}! Te avisamos que tenés un saldo pendiente de $${fmt(cliente.saldo_actual)} con ${nombreNegocio}. Cuando puedas pasá a saldar. Gracias!`
  )
  const phone = formatWaPhone(cliente.telefono)
  return phone ? `https://wa.me/${phone}?text=${texto}` : `https://wa.me/?text=${texto}`
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CuentaCorrientePage() {
  const location = useLocation()
  const { user } = useAuth()
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | null>(null)
  const [busqueda, setBusqueda] = useState('')
  const [soloConDeuda, setSoloConDeuda] = useState(true)

  const { data: config } = useConfiguracion()
  const nombreNegocio = config?.nombre_negocio ?? 'nosotros'

  const { data: todosClientes = [] } = useClientes({ soloActivos: true })

  const clientes = todosClientes.filter(c => {
    const matchDeuda = !soloConDeuda || c.saldo_actual > 0
    const matchBusqueda = !busqueda.trim() || c.nombre.toLowerCase().includes(busqueda.toLowerCase())
    return matchDeuda && matchBusqueda
  })

  const totalAdeudado = todosClientes.reduce((s, c) => s + (c.saldo_actual > 0 ? c.saldo_actual : 0), 0)
  const conDeuda = todosClientes.filter(c => c.saldo_actual > 0).length

  // Abrir cliente desde navegación (ej. click en saldo de ClientesPage)
  useEffect(() => {
    const state = location.state as { clienteId?: string } | null
    if (state?.clienteId && todosClientes.length > 0) {
      const c = todosClientes.find(cl => cl.id === state.clienteId)
      if (c) setClienteSeleccionado(c)
    }
  }, [location.state, todosClientes])

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div className="me-eyebrow" style={{ marginBottom: 4 }}>Comercial</div>
        <h1 className="me-page-title">Cuenta corriente</h1>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div className="me-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
            Clientes con deuda
          </div>
          <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: conDeuda > 0 ? 'var(--me-bad)' : 'var(--me-text)' }}>
            {conDeuda}
          </div>
          <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>de {todosClientes.length} activos</div>
        </div>
        <div className="me-card" style={{ flex: 2, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>
            Total adeudado
          </div>
          <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: totalAdeudado > 0 ? 'var(--me-bad)' : 'var(--me-ok)' }}>
            ${fmt(totalAdeudado)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>saldo pendiente consolidado</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)', display: 'flex', pointerEvents: 'none' }}>
            <SearchIcon size={14} strokeWidth={1.8} />
          </span>
          <input
            className="me-input"
            style={{ width: '100%', paddingLeft: 34 }}
            placeholder="Buscar cliente…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
        <button
          onClick={() => setSoloConDeuda(v => !v)}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid var(--me-line)',
            background: soloConDeuda ? 'var(--me-bad-soft)' : 'none',
            cursor: 'pointer', fontSize: 13,
            color: soloConDeuda ? 'var(--me-bad)' : 'var(--me-text-3)',
            fontFamily: 'var(--me-sans)', fontWeight: soloConDeuda ? 600 : 400,
            transition: 'all var(--me-dur-short)',
          }}
        >
          {soloConDeuda ? 'Con deuda' : 'Todos'}
        </button>
      </div>

      {/* Lista de clientes */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        {clientes.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            {soloConDeuda
              ? 'Sin deudas pendientes — todas las cuentas están saldadas'
              : 'Sin clientes que coincidan con la búsqueda'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Límite CC</th>
                  <th style={{ textAlign: 'right' }}>Saldo deuda</th>
                  <th style={{ textAlign: 'right' }}>Disponible</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {clientes
                  .slice()
                  .sort((a, b) => b.saldo_actual - a.saldo_actual)
                  .map((c, i) => {
                    const disponible = c.limite_credito > 0 ? c.limite_credito - c.saldo_actual : null
                    return (
                      <tr
                        key={c.id}
                        style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)', cursor: 'pointer' }}
                        onClick={() => setClienteSeleccionado(c)}
                      >
                        <td style={{ fontWeight: 500 }}>{c.nombre}</td>
                        <td>
                          {c.tipo === 'mayorista'
                            ? <span className="me-pill me-pill-mayor">Mayorista</span>
                            : <span className="me-pill me-pill-neutral">Minorista</span>}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                          {c.limite_credito > 0 ? `$${fmt(c.limite_credito)}` : <span style={{ color: 'var(--me-text-4)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 700,
                            color: c.saldo_actual > 0 ? 'var(--me-bad)' : 'var(--me-ok)',
                          }}>
                            ${fmt(c.saldo_actual)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                          {disponible !== null
                            ? <span style={{ color: disponible < 0 ? 'var(--me-bad)' : 'var(--me-text-2)' }}>${fmt(disponible)}</span>
                            : <span style={{ color: 'var(--me-text-4)' }}>—</span>}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {c.saldo_actual > 0 && (
                            <a
                              href={buildWaUrl(c, nombreNegocio)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={c.telefono ? `Avisar a ${c.nombre} por WhatsApp` : 'Enviar recordatorio (sin teléfono guardado)'}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 30, height: 30, borderRadius: 8,
                                background: 'var(--me-ok-soft)',
                                color: 'var(--me-ok)',
                                textDecoration: 'none',
                                transition: 'all var(--me-dur-short)',
                              }}
                              onMouseOver={e => {
                                ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-ok)'
                                ;(e.currentTarget as HTMLAnchorElement).style.color = '#fff'
                              }}
                              onMouseOut={e => {
                                ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-ok-soft)'
                                ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--me-ok)'
                              }}
                            >
                              <WaIcon />
                            </a>
                          )}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {clienteSeleccionado && (
        <ClienteCcPanel
          cliente={clienteSeleccionado}
          usuarioId={user?.id ?? ''}
          onClose={() => setClienteSeleccionado(null)}
          onClienteUpdated={c => setClienteSeleccionado(c)}
        />
      )}
    </div>
  )
}

// ─── Panel lateral de cuenta corriente ────────────────────────────────────────

function ClienteCcPanel({
  cliente, usuarioId, onClose, onClienteUpdated,
}: {
  cliente: Cliente
  usuarioId: string
  onClose: () => void
  onClienteUpdated: (c: Cliente) => void
}) {
  const { data: movimientos = [], isLoading } = useCcMovimientos(cliente.id)
  const registrar = useRegistrarMovimientoCC()
  const { data: todosClientes = [] } = useClientes({ soloActivos: true })
  const { data: config } = useConfiguracion()
  const nombreNegocio = config?.nombre_negocio ?? 'nosotros'

  const [tipoMov, setTipoMov]   = useState<'abono' | 'cargo'>('abono')
  const [monto, setMonto]       = useState('')
  const [notas, setNotas]       = useState('')
  const [showForm, setShowForm] = useState(false)

  // Sync cliente con datos frescos tras mutación
  useEffect(() => {
    const fresco = todosClientes.find(c => c.id === cliente.id)
    if (fresco) onClienteUpdated(fresco)
  }, [todosClientes])

  async function handleRegistrar() {
    const montoNum = parseFloat(monto)
    if (!montoNum || montoNum <= 0) { toast.error('Ingresá un monto válido'); return }
    try {
      await registrar.mutateAsync({ cliente, tipo: tipoMov, monto: montoNum, notas, usuarioId })
      toast.success(tipoMov === 'abono' ? 'Pago registrado' : 'Cargo registrado')
      setMonto('')
      setNotas('')
      setShowForm(false)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  const excedeLimite = cliente.limite_credito > 0 && cliente.saldo_actual > cliente.limite_credito

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38, background: 'rgba(0,0,0,0.28)' }} />
      <div className="me-drawer-in" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 45,
        width: 500, background: 'var(--me-surface)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--me-line)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div className="me-eyebrow" style={{ marginBottom: 2 }}>Cuenta corriente</div>
              <h2 className="me-section-title" style={{ margin: 0 }}>{cliente.nombre}</h2>
            </div>
            <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
          </div>

          {/* Saldo highlight */}
          <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
            <div style={{
              flex: 1, padding: '10px 14px', borderRadius: 10,
              background: cliente.saldo_actual > 0 ? 'var(--me-bad-soft)' : 'var(--me-ok-soft)',
              border: `1px solid ${cliente.saldo_actual > 0 ? 'rgba(196,43,70,0.15)' : 'rgba(26,138,74,0.15)'}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: cliente.saldo_actual > 0 ? 'var(--me-bad)' : 'var(--me-ok)', marginBottom: 2 }}>
                Saldo deuda
              </div>
              <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 22, fontWeight: 700, color: cliente.saldo_actual > 0 ? 'var(--me-bad)' : 'var(--me-ok)' }}>
                ${fmt(cliente.saldo_actual)}
              </div>
              {excedeLimite && (
                <div style={{ fontSize: 10, color: 'var(--me-bad)', marginTop: 2, fontWeight: 600 }}>
                  Excede límite de ${fmt(cliente.limite_credito)}
                </div>
              )}
            </div>
            {cliente.limite_credito > 0 && (
              <div style={{ flex: 1, padding: '10px 14px', borderRadius: 10, background: 'var(--me-surface-2)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 2 }}>
                  Disponible
                </div>
                <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 22, fontWeight: 700, color: 'var(--me-text-2)' }}>
                  ${fmt(Math.max(0, cliente.limite_credito - cliente.saldo_actual))}
                </div>
                <div style={{ fontSize: 10, color: 'var(--me-text-3)', marginTop: 2 }}>
                  límite ${fmt(cliente.limite_credito)}
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp recordatorio */}
          {cliente.saldo_actual > 0 && (
            <a
              href={buildWaUrl(cliente, nombreNegocio)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, marginTop: 12,
                padding: '9px 16px', borderRadius: 10,
                background: 'var(--me-ok-soft)',
                border: '1px solid rgba(34,197,94,0.25)',
                color: 'var(--me-ok)',
                fontFamily: 'var(--me-sans)', fontSize: 13.5, fontWeight: 600,
                textDecoration: 'none',
                transition: 'all var(--me-dur-short)',
              }}
              onMouseOver={e => {
                ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-ok)'
                ;(e.currentTarget as HTMLAnchorElement).style.color = '#fff'
                ;(e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--me-ok)'
              }}
              onMouseOut={e => {
                ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-ok-soft)'
                ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--me-ok)'
                ;(e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(34,197,94,0.25)'
              }}
            >
              <WaIcon />
              {cliente.telefono
                ? `Avisar deuda por WhatsApp`
                : `Enviar recordatorio (sin teléfono guardado)`}
            </a>
          )}

          {/* Tipo */}
          {cliente.tipo === 'mayorista' && (
            <div style={{ marginTop: 8 }}>
              <span className="me-pill me-pill-mayor">Mayorista</span>
            </div>
          )}
        </div>

        {/* Form rápido registrar movimiento */}
        {showForm ? (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--me-line)', flexShrink: 0, background: 'var(--me-surface-2)' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {(['abono', 'cargo'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipoMov(t)}
                  style={{
                    flex: 1, padding: '7px 12px', borderRadius: 8, border: '2px solid',
                    borderColor: tipoMov === t ? (t === 'abono' ? 'var(--me-ok)' : 'var(--me-bad)') : 'var(--me-line)',
                    background: tipoMov === t ? (t === 'abono' ? 'var(--me-ok-soft)' : 'var(--me-bad-soft)') : 'none',
                    cursor: 'pointer', fontFamily: 'var(--me-sans)', fontSize: 13, fontWeight: 600,
                    color: tipoMov === t ? (t === 'abono' ? 'var(--me-ok)' : 'var(--me-bad)') : 'var(--me-text-3)',
                    transition: 'all var(--me-dur-short)',
                  }}
                >
                  {t === 'abono' ? 'Abono (pago)' : 'Cargo (deuda)'}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--me-text-3)', fontSize: 13, pointerEvents: 'none' }}>$</span>
                <input
                  className="me-input"
                  style={{ width: '100%', paddingLeft: 22, fontFamily: 'var(--me-mono)' }}
                  type="number" min={0.01} step={0.01}
                  placeholder="0,00"
                  value={monto}
                  onChange={e => setMonto(e.target.value)}
                  autoFocus
                />
              </div>
              <input
                className="me-input"
                style={{ flex: 2 }}
                placeholder="Concepto / notas"
                value={notas}
                onChange={e => setNotas(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="me-btn me-btn-ghost" style={{ flex: 1 }} onClick={() => { setShowForm(false); setMonto(''); setNotas('') }}>
                Cancelar
              </button>
              <button
                className="me-btn me-btn-primary"
                style={{ flex: 2 }}
                onClick={handleRegistrar}
                disabled={registrar.isPending}
              >
                {registrar.isPending ? 'Registrando…' : `Registrar ${tipoMov}`}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--me-line)', flexShrink: 0 }}>
            <button
              className="me-btn me-btn-primary"
              style={{ width: '100%' }}
              onClick={() => { setShowForm(true); setTipoMov('abono') }}
            >
              Registrar pago / cargo
            </button>
          </div>
        )}

        {/* Historial */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--me-text-3)' }}>Cargando historial…</div>
          ) : movimientos.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13 }}>
              Sin movimientos en esta cuenta
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {movimientos.map((m, idx) => (
                <MovimientoRow key={m.id} mov={m} zebra={idx % 2 !== 0} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ─── WhatsApp icon ────────────────────────────────────────────────────────────

function WaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

// ─── Fila de movimiento ────────────────────────────────────────────────────────

function MovimientoRow({ mov, zebra }: { mov: CcMovimiento; zebra: boolean }) {
  const esAbono = mov.tipo === 'abono'
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto',
      gap: 12, alignItems: 'center',
      padding: '11px 24px',
      borderBottom: '1px solid var(--me-line-2)',
      background: zebra ? 'var(--me-surface-2)' : 'transparent',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            display: 'inline-block', width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: esAbono ? 'var(--me-ok)' : 'var(--me-bad)',
          }} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {esAbono ? 'Abono' : 'Cargo'}
          </span>
          {mov.referencia_tipo && (
            <span style={{ fontSize: 11, color: 'var(--me-text-3)' }}>· {mov.referencia_tipo}</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 2, paddingLeft: 14 }}>
          {mov.notas ?? fmtFecha(mov.created_at)}
          {mov.notas && <span style={{ marginLeft: 6 }}>· {fmtFecha(mov.created_at)}</span>}
        </div>
      </div>
      <div style={{
        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
        fontSize: 13, fontWeight: 700, textAlign: 'right',
        color: esAbono ? 'var(--me-ok)' : 'var(--me-bad)',
      }}>
        {esAbono ? '-' : '+'} ${fmt(mov.monto)}
      </div>
      <div style={{
        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
        fontSize: 12, color: 'var(--me-text-3)', textAlign: 'right', minWidth: 80,
      }}>
        ${fmt(mov.saldo_posterior)}
      </div>
    </div>
  )
}
